import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  ArrowDownRight,
  Plus,
  Trophy,
  Wallet,
  Zap,
  Image as ImageIcon,
  Headphones,
  Rocket,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CountUp } from "@/components/CountUp";
import { PartnerMarquee } from "@/components/PartnerMarquee";
import { MaskText } from "@/components/MaskText";
import { MarqueeText } from "@/components/MarqueeText";
import { Seo } from "@/components/Seo";
import { FEATURES, STATS, STEPS, VERTICALS, SITE, AFFILIATION } from "@/data/site";
import { BLOG_POSTS } from "@/data/blog";

const HERO_IMG =
  "https://images.unsplash.com/photo-1706675780107-7c43cc487928?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const ABOUT_IMG =
  "https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const VERT_IMAGES = [
  "https://images.unsplash.com/photo-1706675780107-7c43cc487928?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1627831389670-d20f5a01c536?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
];

const EASE = [0.16, 1, 0.3, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.1 + i * 0.08, ease: EASE },
  }),
};

const posts = BLOG_POSTS.slice(0, 3);

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 240]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  return (
    <div data-testid="home-page">
      <Seo
        title="TopAffiliation | Affiliazione Gambling, Scommesse, Casinò e Trading"
        description="Il primo network di affiliazione made in Italy: campagne su scommesse, casinò e trading con le CPA e Revenue più alte del web. Iscrizione gratuita in 1 minuto."
        path="/"
      />

      {/* ================= HERO ================= */}
      <section
        ref={heroRef}
        className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden pt-28 pb-14"
      >
        <div className="absolute inset-0 -z-20 bg-[#0A0A0A]" />
        <div className="absolute inset-0 -z-10 grid-overlay opacity-70" />
        <motion.div
          style={{ y: glowY }}
          className="absolute -z-10 -top-32 -left-24 h-[34rem] w-[34rem] rounded-full bg-brand-red/25 blur-[120px]"
        />
        <motion.div
          style={{ y: glowY }}
          className="absolute -z-10 top-1/3 right-0 h-[26rem] w-[26rem] rounded-full bg-brand-red/10 blur-[120px]"
        />

        <div className="container-x w-full grid lg:grid-cols-12 gap-y-12 lg:gap-10 items-center">
          {/* LEFT */}
          <motion.div style={{ y: textY }} className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-4 mb-8"
            >
              <span className="h-px w-12 bg-brand-red" />
              <span className="eyebrow">Network di affiliazione N°1 in Italia</span>
            </motion.div>

            <h1 className="font-heading font-extrabold leading-[0.9] tracking-[-0.035em] text-[clamp(2.9rem,8.5vw,6.75rem)]">
              <MaskText
                trigger="load"
                delay={0.12}
                stagger={0.1}
                lines={[
                  "Guadagna",
                  "subito con",
                  <span key="brand" className="text-gradient">
                    TopAffiliation
                  </span>,
                ]}
              />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
              className="mt-8 max-w-xl text-lg text-white/65 leading-relaxed"
            >
              Monetizza il tuo sito con il programma di affiliazione dalle commissioni più
              alte del mercato su scommesse, casinò e trading. Aderendo ad{" "}
              <a
                href={AFFILIATION.site}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white font-semibold underline decoration-brand-red decoration-2 underline-offset-4 hover:text-brand-red transition-colors"
              >
                Affiliation Bet
              </a>{" "}
              costruisci una rendita passiva in pochi minuti.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <a
                href={AFFILIATION.signup}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-register"
                className="btn-glow group inline-flex items-center justify-center gap-3 rounded-full bg-brand-red px-8 py-4 text-base font-semibold text-white hover:bg-brand-redDark"
              >
                Iscriviti subito senza impegno
                <span className="grid place-items-center h-6 w-6 rounded-full bg-white/15 transition-transform group-hover:translate-x-1">
                  <ArrowRight className="h-4 w-4" />
                </span>
              </a>
              <Link
                to="/chi-siamo"
                data-testid="hero-cta-about"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-8 py-4 text-base font-semibold text-white hover:border-brand-red hover:text-brand-red transition-colors"
              >
                Scopri di più
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/50"
            >
              <span>25+ anni di esperienza</span>
              <span className="h-1 w-1 rounded-full bg-brand-red" />
              <span>Pagamenti ogni 30 giorni</span>
              <span className="h-1 w-1 rounded-full bg-brand-red" />
              <span>Iscrizione gratuita</span>
            </motion.div>
          </motion.div>

          {/* RIGHT — framed spotlighted photography + parallax */}
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.25, ease: EASE }}
              className="relative"
            >
              <div className="absolute -inset-6 -z-10 rounded-[2.5rem] bg-brand-red/15 blur-3xl" />
              <motion.div
                style={{ y: imgY }}
                className="relative overflow-hidden rounded-[2rem] border border-white/10 aspect-[3/4]"
              >
                <img
                  src={HERO_IMG}
                  alt="Stadio di calcio illuminato di notte — scommesse sportive"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute inset-0 [background:radial-gradient(120%_80%_at_70%_10%,transparent,rgba(0,0,0,0.55))]" />
                <div className="absolute top-5 left-5 flex items-center gap-2 rounded-full bg-black/50 backdrop-blur px-3 py-1.5 text-xs font-semibold text-white/90 border border-white/10">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-red animate-pulse" />
                  LIVE • Scommesse · Casinò · Trading
                </div>
              </motion.div>

              {/* floating KPI card */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
                className="absolute -bottom-6 -left-6 sm:-left-10 w-52 card-surface bg-[#121212]/90 backdrop-blur-xl p-5"
              >
                <div className="font-heading text-4xl font-extrabold text-brand-red leading-none">
                  <CountUp end={25} suffix="+" />
                </div>
                <p className="mt-2 text-xs text-white/60 leading-snug">
                  anni da leader del mercato gambling in Italia
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <div className="container-x w-full mt-14 hidden md:flex items-center gap-3 text-white/40 text-xs uppercase tracking-[0.2em]">
          <ArrowDownRight className="h-4 w-4 text-brand-red" />
          Scorri per esplorare
        </div>
      </section>

      {/* ================= EDITORIAL MARQUEE ================= */}
      <section className="py-8 md:py-10 border-y border-white/10 bg-[#0C0C0C]">
        <MarqueeText
          items={[
            "Commissioni più alte",
            "Pagamenti ogni 30 giorni",
            "Supporto 24/7",
            "Materiale pronto all'uso",
            "Iscrizione gratuita",
          ]}
        />
      </section>

      {/* ================= PARTNERS ================= */}
      <section className="py-14">
        <div className="container-x mb-8">
          <p className="eyebrow">I nostri partner</p>
        </div>
        <PartnerMarquee />
      </section>

      {/* ================= STATS ================= */}
      <section className="py-16 md:py-24 border-y border-white/10">
        <div className="container-x grid grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal
              key={s.label}
              delay={i * 0.08}
              className={`px-2 md:px-8 py-6 ${
                i !== 0 ? "border-l border-white/10" : ""
              } ${i === 2 ? "border-l" : ""}`}
            >
              <div className="font-heading text-5xl md:text-6xl font-extrabold text-white">
                <CountUp end={s.value} suffix={s.suffix} />
              </div>
              <p className="mt-3 text-sm text-white/50 leading-relaxed">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ================= CHAPTER 01 — PERCHÉ (numbered manifesto) ================= */}
      <section className="py-20 md:py-32">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-4">
              <span className="font-heading text-sm font-bold text-brand-red">(01)</span>
              <p className="eyebrow mt-2">Perché sceglierci</p>
            </div>
            <div className="lg:col-span-8">
              <h2 className="font-heading text-3xl md:text-5xl font-extrabold tracking-[-0.02em] leading-[1.02]">
                <MaskText
                  lines={[
                    "Monetizza il tuo traffico",
                    "con la garanzia di un leader.",
                  ]}
                />
              </h2>
            </div>
          </div>

          <div className="border-t border-white/10">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title}>
                <div className="group grid grid-cols-[auto_1fr] md:grid-cols-[5rem_1fr_auto] items-start gap-5 md:gap-8 py-8 border-b border-white/10 transition-colors hover:bg-white/[0.02] px-0 md:px-3">
                  <span className="font-heading text-2xl md:text-3xl font-extrabold text-white/20 group-hover:text-brand-red transition-colors">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <div className="flex items-center gap-3">
                      <f.icon className="h-5 w-5 text-brand-red" />
                      <h3 className="font-heading text-xl md:text-2xl font-bold">
                        {f.title}
                      </h3>
                    </div>
                    <p className="mt-3 text-white/55 leading-relaxed max-w-2xl">
                      {f.desc}
                    </p>
                  </div>
                  <ArrowRight className="hidden md:block h-6 w-6 text-white/20 self-center transition-all group-hover:text-brand-red group-hover:translate-x-1" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CHAPTER 02 — COME FUNZIONA ================= */}
      <section className="py-20 md:py-32 bg-[#0C0C0C] border-y border-white/10">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-4">
              <span className="font-heading text-sm font-bold text-brand-red">(02)</span>
              <p className="eyebrow mt-2">Come funziona</p>
            </div>
            <div className="lg:col-span-8">
              <h2 className="font-heading text-3xl md:text-5xl font-extrabold tracking-[-0.02em] leading-[1.02]">
                <MaskText lines={["Dalla registrazione", "al guadagno in 3 passi."]} />
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden">
            {STEPS.map((s, i) => (
              <Reveal key={s.step} delay={i * 0.1}>
                <div className="relative bg-[#0C0C0C] p-8 md:p-10 h-full min-h-[16rem] flex flex-col">
                  <span className="font-heading text-7xl md:text-8xl font-extrabold text-white/[0.06] leading-none">
                    {s.step}
                  </span>
                  <div className="mt-auto">
                    <s.icon className="h-7 w-7 text-brand-red" />
                    <h3 className="mt-4 font-heading text-2xl font-bold">{s.title}</h3>
                    <p className="mt-3 text-white/55 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CHAPTER 03 — MERCATI (spotlighted photography) ================= */}
      <section className="py-20 md:py-32">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-4">
              <span className="font-heading text-sm font-bold text-brand-red">(03)</span>
              <p className="eyebrow mt-2">I nostri mercati</p>
            </div>
            <div className="lg:col-span-8">
              <h2 className="font-heading text-3xl md:text-5xl font-extrabold tracking-[-0.02em] leading-[1.02]">
                <MaskText lines={["I migliori bookmaker", "italiani e internazionali."]} />
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {VERTICALS.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="group relative overflow-hidden rounded-2xl border border-white/10 aspect-[4/5]">
                  <img
                    src={VERT_IMAGES[i]}
                    alt={v.title}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-90" />
                  <div className="absolute inset-0 [background:radial-gradient(120%_70%_at_50%_120%,rgba(213,27,38,0.35),transparent)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute inset-0 p-7 flex flex-col justify-end">
                    <v.icon className="h-7 w-7 text-brand-red" />
                    <h3 className="mt-4 font-heading text-2xl font-bold">{v.title}</h3>
                    <p className="mt-2 text-sm text-white/60 leading-relaxed">{v.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= ABOUT (parallax framed) ================= */}
      <AboutBand />

      {/* ================= BLOG ================= */}
      <section className="py-20 md:py-32 bg-[#0C0C0C] border-y border-white/10">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <span className="font-heading text-sm font-bold text-brand-red">(04)</span>
              <h2 className="mt-2 font-heading text-3xl md:text-5xl font-extrabold tracking-[-0.02em]">
                <MaskText lines={["Dal blog"]} />
              </h2>
            </div>
            <Link
              to="/blog"
              className="group inline-flex items-center gap-2 text-brand-red font-semibold"
            >
              Tutti gli articoli
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {posts.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`home-blog-card-${p.slug}`}
                  className="group block h-full"
                >
                  <div className="overflow-hidden rounded-2xl border border-white/10 aspect-[16/10]">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <span className="mt-5 inline-block text-xs font-semibold uppercase tracking-widest text-brand-red">
                    {p.category}
                  </span>
                  <h3 className="mt-2 font-heading text-xl font-bold leading-snug group-hover:text-brand-red transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm text-white/50 line-clamp-2">{p.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section className="py-24 md:py-40">
        <div className="container-x text-center">
          <p className="eyebrow">Pronto a partire?</p>
          <h2 className="mt-6 font-heading font-extrabold tracking-[-0.03em] leading-[0.95] text-[clamp(2.5rem,7vw,5.5rem)]">
            <MaskText
              lines={[
                "Trasforma il tuo",
                <span key="c" className="text-gradient">
                  sito in profitto.
                </span>,
              ]}
            />
          </h2>
          <p className="mt-7 text-white/60 max-w-xl mx-auto text-lg">
            Iscrizione gratuita in 1 minuto. Entra oggi nella community di {SITE.name}.
          </p>
          <a
            href={AFFILIATION.signup}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-cta-register"
            className="btn-glow group mt-10 inline-flex items-center gap-3 rounded-full bg-brand-red px-9 py-4 text-base font-semibold text-white hover:bg-brand-redDark"
          >
            Iscriviti subito senza impegno
            <span className="grid place-items-center h-6 w-6 rounded-full bg-white/15 transition-transform group-hover:translate-x-1">
              <ArrowRight className="h-4 w-4" />
            </span>
          </a>
        </div>
      </section>
    </div>
  );
}

function AboutBand() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section ref={ref} className="py-20 md:py-32">
      <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="relative">
            <div className="absolute -inset-6 -z-10 rounded-[2.5rem] bg-brand-red/10 blur-3xl" />
            <div className="relative overflow-hidden rounded-[2rem] border border-white/10 aspect-[4/3]">
              <motion.img
                style={{ y, scale: 1.12 }}
                src={ABOUT_IMG}
                alt="Il team di TopAffiliation al lavoro"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 [background:radial-gradient(120%_80%_at_30%_20%,transparent,rgba(0,0,0,0.5))]" />
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <span className="font-heading text-sm font-bold text-brand-red">(chi siamo)</span>
          <h2 className="mt-3 font-heading text-3xl md:text-5xl font-extrabold tracking-[-0.02em] leading-[1.03]">
            Leader italiano del gambling da oltre 25 anni
          </h2>
          <p className="mt-6 text-white/60 leading-relaxed max-w-xl">
            Proponiamo le migliori condizioni per strategie di affiliazione di qualità:
            piattaforma veloce e intuitiva, assistenza 24/7 e commissioni tra le più alte
            del web. Migliaia di clienti italiani ci hanno scelto.
          </p>
          <Link
            to="/chi-siamo"
            data-testid="about-teaser-link"
            className="group mt-8 inline-flex items-center gap-2 text-brand-red font-semibold"
          >
            La nostra storia
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
